<template>
  <div :class="{ 'stats': true, 'minimal-mode': !showChart, 'is-loading': loadingData }">
    <div class="stats-col">
      <stats-box
        color="primary"
        :title="$t('stats.reservations')"
        iconName="calendar-big"
        :value="statsReservations"
        :footerTitle="$t('stats.change')"
        :minimalMode="!showChart"
        :footerValue="statsReservationsPercent + '%'"
        :trend="true" />

      <stats-box
        color="success"
        :title="$t('stats.accepted')"
        iconName="check-circle-big"
        :value="statsAccepted"
        :footerTitle="$t('stats.percentTotal')"
        :minimalMode="!showChart"
        :footerValue="statsAcceptedPercent + '%'" />

      <stats-box
        v-if="!paymentsEnabled"
        color="warning"
        :title="$t('stats.cancelled')"
        iconName="cross-circle"
        :value="statsCancelled"
        :footerTitle="$t('stats.percentTotal')"
        :minimalMode="!showChart"
        :footerValue="statsCancelledPercent + '%'"
        :trend="true" />

      <stats-box
        v-if="paymentsEnabled && userCanSeeFullPaymentsData"
        color="warning"
        :title="$t('stats.trade')"
        iconName="chart-big"
        :value="statsCashTurnover + ' ' + $t('common.ui.incomeCurrency')"
        :footerTitle="$t('stats.change')"
        :minimalMode="!showChart"
        :footerValue="statsCachTurnoverPercent + '%'"
        :trend="true" />

      <stats-box
        color="info"
        :title="$t('stats.newClients')"
        iconName="profile"
        :value="statsNewClients"
        :expanded="paymentsEnabled && !userCanSeeFullPaymentsData"
        :footerTitle="$t('stats.allTotal')"
        :minimalMode="!showChart"
        :footerValue="statsNewClientsAll" />
    </div>

    <div
      v-if="showChart"
      class="stats-col">
      <stats-chart ref="chart" />
    </div>

    <a
      href="#"
      class="toggler"
      @click.prevent="toggleStats">
      <template v-if="showChart">{{ $t('stats.hideStats') }}</template>
      <template v-if="!showChart">{{ $t('stats.showStats') }}</template>
    </a>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';
import UserTypes from './../mixins/UserTypes';
import SanitizeUtils from './../helpers/SanitizeUtils';
import StatsBox from './StatsBox';
import StatsChart from './StatsChart';

export default {
  name: 'stats',
  mixins: [
    UserTypes
  ],
  components: {
    'stats-box': StatsBox,
    'stats-chart': StatsChart
  },
  data () {
    return {
      showChart: true,
      dataLoaded: false,
      dateStart: '',
      dateEnd: '',
      statsReservations: '-',
      statsReservationsPercent: '-',
      statsAccepted: '-',
      statsAcceptedPercent: '-',
      statsCancelled: '-',
      statsCancelledPercent: '-',
      statsNewClients: '-',
      statsNewClientsAll: '-',
      statsCashTurnover: '-',
      statsCachTurnoverPercent: '-',
      loadingData: true
    };
  },
  computed: {
    paymentsEnabled () {
      return !!this.$store.state.config.company.payment;
    }
  },
  mounted () {
    let currentTime = new Date();
    let weekAgo = new Date(currentTime.getTime() - (60 * 60 * 24 * 7 * 1000));
    this.dateStart = [weekAgo.getFullYear(), weekAgo.getMonth() + 1, weekAgo.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    this.dateEnd = [currentTime.getFullYear(), currentTime.getMonth() + 1, currentTime.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    this.showChart = SanitizeUtils.getLocalStorageValue('bookero-dashboard-hide-stats', 'boolean', true);
    this.$bus.$on('dashboard-stats-date-range-changed', this.statsDateRangeChanged);

    setTimeout(() => {
      this.loadData();
    }, 0);
  },
  methods: {
    statsDateRangeChanged (datesRange) {
      this.setDates(datesRange.startDate, datesRange.endDate);
    },
    toggleStats (e) {
      e.preventDefault();
      this.showChart = !this.showChart;
      localStorage.setItem('bookero-dashboard-hide-stats', this.showChart);

      setTimeout(() => {
        if (this.$refs.chart) {
          this.$refs.chart.setData(this.dateStart, this.dateEnd);
        }
      }, 50);
    },
    setDates (startDate, endDate) {
      this.dateStart = [startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      this.dateEnd = [endDate.getFullYear(), endDate.getMonth() + 1, endDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      this.loadData();
    },
    loadData () {
      this.$http.get('/panel/stats/stats-dashboard', {
        params: {
          'date_from': this.dateStart,
          'date_to': this.dateEnd
        }
      }).then(response => {
        this.statsReservations = response.data.reservations.reservations;
        this.statsReservationsPercent = response.data.reservations.percent;
        this.statsAccepted = response.data.accepted.reservations;
        this.statsAcceptedPercent = response.data.accepted.percent;
        this.statsCancelled = response.data.cancelled.reservations;
        this.statsCancelledPercent = response.data.cancelled.percent;
        this.statsNewClients = response.data.new_clients.clients;
        this.statsNewClientsAll = response.data.new_clients.all;

        if (response.data.cash_turnover) {
          this.statsCashTurnover = response.data.cash_turnover.current;
          this.statsCachTurnoverPercent = response.data.cash_turnover.percent;
        }

        this.loadingData = false;
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/stats/stats-dashboard', this.$router);
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('dashboard-stats-date-range-changed', this.statsDateRangeChanged);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.stats {
  border-bottom: 1px solid $color-border-light;
  display: flex;
  justify-content: space-between;
  padding: 3rem 0;
  position: relative;

  &-col {
    display: flex;
    flex-wrap: wrap;
    width: 48%;

    &-three {

    }
  }

  .toggler {
    bottom: .85rem;
    font-size: 1.2rem;
    position: absolute;
    right: 0;
  }

  &.minimal-mode {
    .stats-col {
      flex-wrap: nowrap;

      &:first-child {
        width: 100%;

        .stats-box {
          margin-bottom: 0;
          width: calc(25% - 1.25rem);

          &:nth-child(3) {
            margin-left: 2.5rem;
          }
        }
      }
    }
  }

  &.is-loading {
    .stats-box,
    .chart {
      &:before {
        background: $color-bg-medium-alt;
        border-radius: $border-radius-small;
        content: "";
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        transform: scale(1.01);
        width: 100%;
        z-index: 100;
      }

      &:after {
        animation: spinner 1s infinite linear;
        border: 6px solid $color-text-lightest;
        border-left: 6px solid transparent;
        border-radius: 50%;
        content: "";
        display: block;
        height: 24px;
        left: calc(50% - 12px);
        position: absolute;
        top: calc(50% - 12px);
        width: 24px;
        z-index: 101;
      }
    }
  }
}

@keyframes spinner {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1040px) {
  .stats {
    flex-wrap: wrap;

    &-col {
      margin-bottom: 3rem;
      width: 100%;
    }

    &.minimal-mode {
      .stats-col {
        &:first-child {
          flex-wrap: wrap;
          justify-content: space-between;

          .stats-box {
            margin: 1rem 0!important;
            width: 49%;
          }
        }
      }
    }

    .toggler {
      bottom: 3rem;
    }
  }
}

@media (max-width: 640px) {
  .stats {
    padding: 1rem 0 5rem 0;

    .stats-box {
      margin: 1rem 0!important;
      width: 100%;
    }

    .stats-col {
      margin-bottom: 1rem;
    }

    &.minimal-mode {
      .stats-col {
        &:first-child {
          .stats-box {
            margin: 1rem 0!important;
            width: 100%;
          }
        }
      }
    }

    .chart {
      padding: .5rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/Stats.vue